import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createBlock as _createBlock, unref as _unref, createVNode as _createVNode, resolveComponent as _resolveComponent } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "flex flex-col gap-8 md:gap-6"
}
const _hoisted_2 = {
  key: 0,
  "data-testid": "addressComponentTitle",
  class: "text-input-title"
}

import { watch, ref, onMounted, computed } from 'vue';
import OverseasOption from './OverseasOption.vue';
import AddressLookup from './AddressLookup.vue';
import ManualAddressForm from './ManualAddressForm.vue';
import { AddressLookupOptionsProps, RACQAddressResult } from '@/interfaces';
import {
  addressLookupEmits,
  emitManualAddressSelected,
  emitAddressSelected,
  useMultiInstanceAddressOptionsStore,
  emitAddressReset,
} from '@/store';
import { convertSitecoreFormFieldToVueFormField } from '@/composables/useFormInputData';
import { v4 } from 'uuid';
import { toSafeDisplayPostcode } from '@/utils/convert';
import { AUSTRALIA, AUSTRALIA_ISO } from '@/constants/general-words.const';


export default /*@__PURE__*/_defineComponent({
  ...{
  inheritAttrs: false,
},
  __name: 'AddressLookupOptions',
  props: {
    formInputId: {},
    innerClass: {},
    streetNoRequired: {},
    streetRequired: {},
    suburbRequired: {},
    stateRequired: {},
    postcodeRequired: {},
    countryRequired: {},
    checkAddressRequired: {},
    selectSuburbRequired: {},
    invalidInsuranceRiskSuburbMessageTitle: {},
    invalidInsuranceRiskSuburbMessageText: {},
    noPostcodeMatchesFoundMessage: {},
    postalSameAsInsuredAddressLabel: {},
    riskAddressNotFullyVerifiedMessage: {},
    riskAddressCycloneCheckFailedMessage: {},
    physicalAddressOnlyPOBoxInvalidMessage: {},
    addressTitle: {},
    overseasOptionLabel: {},
    riskSuburbsLabel: {},
    overseasOptionTooltip: {},
    postContent: {},
    validationRegex: {},
    validationRegexErrorMessage: {},
    postcodeInvalid: {},
    postalAddressTooltipVariant: {},
    riskAddressTooltipVariant: {},
    physicalAddressTooltipVariant: {},
    config: {},
    initialPostalAddress: {},
    initialInsuredAddress: {},
    labelClass: {},
    isOversea: { type: Boolean },
    defaultIsOverseasOption: { type: Boolean },
    showManualEntries: { type: Boolean },
    instanceIdentifier: {},
    throwJpForNonQldAddress: {},
    id: {},
    name: {},
    label: {},
    inputType: {},
    placeholderText: {},
    preContent: {},
    postContentClass: {},
    preContentClass: {},
    tooltipContent: {},
    tooltipModalTitle: {},
    tooltipShowInModal: {},
    fields: {},
    emptyMessage: {},
    validation: {},
    outerClass: {},
    refData: {},
    prefixIcon: {},
    suffixIcon: {},
    helpXs: {},
    helpSm: {},
    helpMd: {},
    disablePaste: {},
    classesOptions: {},
    showClearIcon: {},
    autocomplete: {},
    options: {},
    optionsClass: {},
    defaultOption: {},
    inputInnerClass: {},
    isRequired: {},
    isRequiredErrorMessage: {},
    mustAccept: {},
    mustAcceptErrorMessage: {},
    mask: {},
    isHidden: { type: Boolean },
    showPostContent: { type: Boolean },
    dynamicOptions: {},
    elementId: {},
    titleRequiredMessage: {},
    firstnameRequiredErrorMessage: {},
    firstnameRegex: {},
    firstnameRegexErrorMessage: {},
    lastnameRequiredErrorMessage: {},
    lastnameRegex: {},
    lastnameRegexErrorMessage: {},
    format: {},
    pickerOnly: {},
    minDateToday: {},
    maxDateByDays: {},
    icon: {},
    title: {},
    subTitle: {},
    decimals: {},
    minDecimals: {},
    noDecimalsErrorMessage: {},
    min: {},
    max: {},
    minlength: {},
    maxlength: {},
    promoCards: {},
    heading: {},
    cardLayout: {},
    maxItemsToShow: {}
  },
  emits: addressLookupEmits,
  setup(__props: any, { expose: __expose, emit: __emit }) {

const emit = __emit;

const props = __props;



const addressLookupAutoProps = convertSitecoreFormFieldToVueFormField(props as any, props.formInputId.value);
const multiInstanceAddressStore = useMultiInstanceAddressOptionsStore();
const activeAddressComponent = ref<Partial<RACQAddressResult>>({});
const identifier = ref(props.instanceIdentifier || v4());
const store = computed(() =>
  multiInstanceAddressStore.itemIds.includes(identifier.value)
    ? multiInstanceAddressStore.getInstance(identifier.value)
    : undefined
);

onMounted(() => {
  if (!store.value) multiInstanceAddressStore.addInstance(identifier.value);
  if (props.config)
    store.value?.setupAddressComponent(props.config, props.initialInsuredAddress, props.initialPostalAddress);
});

__expose({
  getStore: () => store.value,
});

/*
Decision maker as to whether auto or manual address is used.
*/
watch(
  () => store.value?.useAddress,
  async (newValue) => {
    const newAddress = newValue as RACQAddressResult;
    if (!store.value) return;
    if (store.value.isValidAddress(newAddress)) {
      if (!newValue?.dpid) {
        newAddress.postcode = toSafeDisplayPostcode(newAddress.postcode);
        store.value.setManualAddress(newAddress);
        //When manual address set the address line to 'Not on list...'
        store.value.autoAddressComponents = {
          addressLine: props?.postContent?.value,
        };
      } else {
        store.value.setAutoAddress(newAddress);
      }
    } else {
      store.value.isOverseasSelection = props.defaultIsOverseasOption;
    }
  },
  { once: true }
);

function onManualAddressSelected(value: Partial<RACQAddressResult>) {
  if (!value?.country) value!.country = AUSTRALIA;
  if (!value?.countryISO) value!.countryISO = AUSTRALIA_ISO;

  activeAddressComponent.value = value;
  emit(emitManualAddressSelected, value);
  if (!store.value) return;
  store.value.manualEditEnabled = false;
}

function onAddressSelected(value: Partial<RACQAddressResult>) {
  activeAddressComponent.value = value;
  emit(emitAddressSelected, value);
}

function onAddressReset() {
  activeAddressComponent.value = {};
  emit(emitAddressReset);
}

return (_ctx: any,_cache: any) => {
  const _component_FormKit = _resolveComponent("FormKit")!

  return (store.value)
    ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
        (store.value.showTitle())
          ? (_openBlock(), _createElementBlock("p", _hoisted_2, _toDisplayString(props.addressTitle.value), 1))
          : _createCommentVNode("", true),
        (store.value.config?.allowOverseasAddress)
          ? (_openBlock(), _createBlock(OverseasOption, {
              key: 1,
              defaultIsOverseasOption: _ctx.defaultIsOverseasOption,
              instanceIdentifier: identifier.value,
              overseasOptionLabel: _ctx.overseasOptionLabel?.value,
              overseasOptionTooltip: _ctx.overseasOptionTooltip?.value,
              onAddressReset: _cache[0] || (_cache[0] = () => onAddressReset())
            }, null, 8, ["defaultIsOverseasOption", "instanceIdentifier", "overseasOptionLabel", "overseasOptionTooltip"]))
          : _createCommentVNode("", true),
        _createVNode(AddressLookup, {
          bindings: _unref(addressLookupAutoProps),
          defaultIsOverseasOption: _ctx.defaultIsOverseasOption,
          streetNoRequired: _ctx.streetNoRequired?.value,
          streetRequired: _ctx.streetRequired?.value,
          suburbRequired: _ctx.suburbRequired?.value,
          postcodeRequired: _ctx.postcodeRequired?.value,
          countryRequired: _ctx.countryRequired?.value,
          checkAddressRequired: _ctx.checkAddressRequired?.value,
          selectSuburbRequired: _ctx.selectSuburbRequired?.value,
          stateRequired: _ctx.stateRequired?.value,
          postcodeInvalid: _ctx.postcodeInvalid?.value,
          postalSameAsInsuredAddressLabel: _ctx.postalSameAsInsuredAddressLabel?.value,
          invalidInsuranceRiskSuburbMessageTitle: _ctx.invalidInsuranceRiskSuburbMessageTitle?.value,
          invalidInsuranceRiskSuburbMessageText: _ctx.invalidInsuranceRiskSuburbMessageText?.value,
          riskAddressNotFullyVerifiedMessage: _ctx.riskAddressNotFullyVerifiedMessage?.value,
          riskAddressCycloneCheckFailedMessage: _ctx.riskAddressCycloneCheckFailedMessage?.value,
          instanceIdentifier: identifier.value,
          postalAddressTooltipVariant: _ctx.postalAddressTooltipVariant?.value,
          riskAddressTooltipVariant: _ctx.riskAddressTooltipVariant?.value,
          physicalAddressTooltipVariant: _ctx.physicalAddressTooltipVariant?.value,
          throwJpForNonQldAddress: _ctx.throwJpForNonQldAddress,
          onAddressSelected: _cache[1] || (_cache[1] = (v) => onAddressSelected(v))
        }, null, 8, ["bindings", "defaultIsOverseasOption", "streetNoRequired", "streetRequired", "suburbRequired", "postcodeRequired", "countryRequired", "checkAddressRequired", "selectSuburbRequired", "stateRequired", "postcodeInvalid", "postalSameAsInsuredAddressLabel", "invalidInsuranceRiskSuburbMessageTitle", "invalidInsuranceRiskSuburbMessageText", "riskAddressNotFullyVerifiedMessage", "riskAddressCycloneCheckFailedMessage", "instanceIdentifier", "postalAddressTooltipVariant", "riskAddressTooltipVariant", "physicalAddressTooltipVariant", "throwJpForNonQldAddress"]),
        _createVNode(ManualAddressForm, {
          name: _ctx.name,
          streetNoRequired: _ctx.streetNoRequired?.value,
          streetRequired: _ctx.streetRequired?.value,
          suburbRequired: _ctx.suburbRequired?.value,
          postcodeRequired: _ctx.postcodeRequired?.value,
          countryRequired: _ctx.countryRequired?.value,
          checkAddressRequired: _ctx.checkAddressRequired?.value,
          selectSuburbRequired: _ctx.selectSuburbRequired?.value,
          stateRequired: _ctx.stateRequired?.value,
          invalidInsuranceRiskSuburbMessageTitle: _ctx.invalidInsuranceRiskSuburbMessageTitle?.value,
          invalidInsuranceRiskSuburbMessageText: _ctx.invalidInsuranceRiskSuburbMessageText?.value,
          riskAddressNotFullyVerifiedMessage: _ctx.riskAddressNotFullyVerifiedMessage?.value,
          riskAddressCycloneCheckFailedMessage: _ctx.riskAddressCycloneCheckFailedMessage?.value,
          riskSuburbsLabel: _ctx.riskSuburbsLabel?.value,
          instanceIdentifier: identifier.value,
          noPostcodeMatchesFoundMessage: _ctx.noPostcodeMatchesFoundMessage?.value,
          invalidCharactersRegex: _ctx.validationRegex?.value,
          invalidCharactersMessage: _ctx.validationRegexErrorMessage?.value,
          postcodeInvalid: _ctx.postcodeInvalid?.value,
          throwJpForNonQldAddress: _ctx.throwJpForNonQldAddress,
          physicalAddressOnlyPOBoxInvalidMessage: _ctx.physicalAddressOnlyPOBoxInvalidMessage?.value,
          onManualAddressSelected: _cache[2] || (_cache[2] = (v) => onManualAddressSelected(v))
        }, null, 8, ["name", "streetNoRequired", "streetRequired", "suburbRequired", "postcodeRequired", "countryRequired", "checkAddressRequired", "selectSuburbRequired", "stateRequired", "invalidInsuranceRiskSuburbMessageTitle", "invalidInsuranceRiskSuburbMessageText", "riskAddressNotFullyVerifiedMessage", "riskAddressCycloneCheckFailedMessage", "riskSuburbsLabel", "instanceIdentifier", "noPostcodeMatchesFoundMessage", "invalidCharactersRegex", "invalidCharactersMessage", "postcodeInvalid", "throwJpForNonQldAddress", "physicalAddressOnlyPOBoxInvalidMessage"]),
        _createVNode(_component_FormKit, {
          modelValue: activeAddressComponent.value,
          "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((activeAddressComponent).value = $event)),
          type: "hidden",
          name: _ctx.formInputId.value
        }, null, 8, ["modelValue", "name"])
      ]))
    : _createCommentVNode("", true)
}
}

})